import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CheshireWestAndChesterCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cheshire West & Chester Pothole Claims | 17.77% U-Roads RED | Fixtyer"
        description="Cheshire West and Chester Council's own survey shows U-road RED condition more than doubled to 17.77% in 2024, while B/C roads hit 16.87% RED. GREEN spend but AMBER condition — see what the DfT rating means for your pothole claim."
        canonical="/councils/cheshire-west-and-chester"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Cheshire West and Chester', url: '/councils/cheshire-west-and-chester' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Cheshire West spends above its DfT allocation with a GREEN spend rating — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition — and Cheshire West is AMBER overall because its network is deteriorating. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,313km — 57.5% of the Cheshire West network — and are surveyed annually under Gaist condition surveys. RED-condition U-roads rose steadily from 6.84% in 2020 to 8.35% in 2023, then jumped to 17.77% in 2024 after the council adopted an early version of the new national data standard. Even the pre-2024 trend shows one in twelve U-roads in the worst category.',
          },
          {
            question: 'Does the 2024 spike in RED condition figures weaken my claim?',
            answer: 'No — the council itself says it "opted to take [the new data standard] onboard early which can be seen reflected in this year\'s figures," so 2024 RED percentages are not directly comparable to earlier years. But the council also admits "a continued migration from the greens in grades 1 & 2 towards the ambers in grades 3 & 4" and "small increases in the grade 5 across the board" — documented deterioration regardless of the headline jump.',
          },
          {
            question: 'Does Best Practice AMBER mean the council has a strong Section 58 defence?',
            answer: 'Cheshire West has a documented Asset Management Strategy, 100% annual Gaist surveys across all road classes, and ISO-aligned lifecycle planning — all of which support a Section 58 defence. But Best Practice is AMBER, not GREEN, and the council acknowledges a new maintenance contract in 2023/24 "limited the amount of preventative maintenance we could undertake." Your claim still turns on the specific defect, not the council\'s paperwork.',
          },
          {
            question: 'Cheshire West filled 36,527 potholes in four years — does that mean the roads are fixed?',
            answer: 'No. The council filled 9,837 potholes in 2021/22, 6,252 in 2022/23, 11,870 in 2023/24, and 8,568 in 2024/25 — a four-year total of 36,527 reactive repairs on a network where condition data shows continued decline. A network producing potholes at that rate is one where defects routinely form between inspections.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Cheshire West and Chester Council"
        councilSlug="cheshire-west-and-chester"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/cheshire-west-and-chester"
        headline="Cheshire West and Chester Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Cheshire West and Chester</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Cheshire West: GREEN Spend, Surging RED Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Cheshire West and Chester invests above its DfT allocation and earns a GREEN spend scorecard.
            Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because the council's
            own Gaist surveys show <strong className="text-rose-400">17.77% of unclassified roads in RED
            condition</strong> in 2024, B and C roads at 16.87% RED, and
            <strong className="text-rose-400"> 36,527 potholes filled in four years</strong> while the network
            keeps deteriorating.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">17.77%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 8.35% in 2023 — more than double. The council adopted the new national data standard
              early, but also admits a "continued migration" from good to poor condition across the network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of Gaist survey data from Cheshire West's own transparency report — a network
            sliding from green towards amber and red
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (2,284km total)</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">330km</div>
                <div className="text-sm text-surface-600">A-roads (14.4%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">641km</div>
                <div className="text-sm text-surface-600">B and C roads (28.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1,313km</div>
                <div className="text-sm text-surface-600">U-roads (57.5%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              More than half the borough's carriageway is unclassified — estate roads, village routes and
              residential streets where most pothole damage claims originate.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (330km — 14.4% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.39%</td>
                    <td className="px-4 py-2 text-center">61.53%</td>
                    <td className="px-4 py-2 text-center">37.03%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.45%</td>
                    <td className="px-4 py-2 text-center">66.76%</td>
                    <td className="px-4 py-2 text-center">30.81%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.34%</td>
                    <td className="px-4 py-2 text-center">66.96%</td>
                    <td className="px-4 py-2 text-center">31.58%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.45%</td>
                    <td className="px-4 py-2 text-center">69.91%</td>
                    <td className="px-4 py-2 text-center">28.64%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.66%</td>
                    <td className="px-4 py-2 text-center font-bold">68.14%</td>
                    <td className="px-4 py-2 text-center font-bold">25.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition quadrupled from 1.45% to 6.66% in a single year. Green roads fell from
              37% to 25%. Even on the best-maintained routes, the trend is wrong.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (641km — 28.1% of network): sharply declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.2%</td>
                    <td className="px-4 py-2 text-center">70.78%</td>
                    <td className="px-4 py-2 text-center">24.89%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.2%</td>
                    <td className="px-4 py-2 text-center">74.56%</td>
                    <td className="px-4 py-2 text-center">19.76%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.25%</td>
                    <td className="px-4 py-2 text-center">75.74%</td>
                    <td className="px-4 py-2 text-center">18.94%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.29%</td>
                    <td className="px-4 py-2 text-center">76.27%</td>
                    <td className="px-4 py-2 text-center">18.34%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">16.87%</td>
                    <td className="px-4 py-2 text-center font-bold">67.25%</td>
                    <td className="px-4 py-2 text-center font-bold">15.88%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C RED roads more than tripled from 5.29% to 16.87%. Good-condition roads halved from 24.89%
              to 15.88% since 2020. Over three-quarters of B/C roads were already amber before the 2024 spike.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£15.5m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£20.5m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">79%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Cheshire West spends <span className="text-emerald-400">32% above its DfT allocation</span> on
              capital maintenance — and local roads are still declining. The problem isn't the chequebook.
              The network is deteriorating faster than even a GREEN-rated spend programme can repair it.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,313km U-Road Problem
          </h2>
          <p className="text-surface-600 mb-8">
            57.5% of the network is unclassified roads — surveyed annually, but steadily worsening
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6.84%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7.49%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8.00%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8.35%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17.77%*</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Steady Pre-2024 Climb</h3>
              <p className="text-surface-600 text-sm mb-3">
                Before the methodology change, U-road RED condition rose every single year — from 6.84%
                in 2020 to 8.35% in 2023. That is roughly 90km of residential and village roads in the
                worst category, climbing year on year.
              </p>
              <p className="text-surface-600 text-sm">
                Cheshire West surveys 100% of U-roads annually via Gaist — so the council cannot claim
                it lacks condition data for these roads. The question is what it did with that knowledge.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 2024 Methodology Change</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council adopted the emerging PAS 2161 data standard early in 2024. It states this
                "can be seen reflected in this year's figures" — meaning the jump from 8.35% to 17.77%
                is partly a different ruler, not purely a one-year collapse.
              </p>
              <p className="text-amber-800 text-sm">
                But the council also admits "a continued migration from the greens in grades 1 &amp; 2
                towards the ambers in grades 3 &amp; 4" — documented deterioration under either standard.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Cheshire West's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Your U-road was surveyed annually — what did the Gaist data show for your street?</li>
              <li>• If 8.35% of U-roads were RED in 2023, what preventative work was planned for yours?</li>
              <li>• With grade 5 increasing "across the board," why wasn't the defect on your road caught?</li>
              <li>• Does the 2024 methodology change mean pre- and post-2024 records can be compared fairly?</li>
            </ul>
            <p className="text-white font-medium">
              A council that surveys every U-road annually and still sees RED condition climbing has
              documented knowledge of network-wide deterioration — which raises the bar for what
              "reasonable" inspection and repair looks like on your specific road.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            36,527 Potholes in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">9,837</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,252</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,870</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">8,568</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">1,657 (as of end June — partial year)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36,527</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~25 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over four full years, Cheshire West fills around 25 potholes per day across
                its 2,284km network. The 2023/24 peak of 11,870 — roughly 32 per day — coincided with
                the council starting a new term maintenance contract that "limited the amount of
                preventative maintenance we could undertake."
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Permanent Repairs Budget</h3>
              <p className="text-amber-800 text-sm">
                The council allocates £2.6m for "Pothole permanent repairs" in its 2025/26 capital
                programme — alongside £4.65m for preventative treatments and £1.145m for resurfacing.
                Reactive repair is funded, but the condition data shows it is not keeping pace with
                deterioration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Methodology Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council's Own Deterioration Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Cheshire West explains the condition trend and the 2024 methodology shift — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Historically we have reported the grade five as the red but with the new data standard have opted to take this onboard early which can be seen reflected in this year's figures."
              source="Cheshire West and Chester Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The data show a continued migration from the greens in grades 1 & 2 towards the ambers in grades 3 & 4, this can also be seen in the small increases in the grade 5 across the board."
              source="Cheshire West and Chester Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="In 2023/24 we started a new term maintenance contract which limited the amount of preventative maintenance we could undertake and with late additional funding from the DFT this pushed up the length of resurfacing undertaken."
              source="Cheshire West and Chester Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Cheshire West formally acknowledges that road condition is migrating from good to poor
                across all road classes, that a contract change reduced preventative work in 2023/24,
                and that the 2024 RED figures reflect an early methodology change — not a sudden recovery.
              </p>
              <p className="text-surface-600 text-sm">
                Documented knowledge of network-wide deterioration — combined with a maintenance contract
                that limited prevention — raises the standard for what a "reasonable" inspection regime
                looks like under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road in the Gaist survey data showing grade 3, 4 or 5 deterioration?</li>
                <li>• Did the 2023/24 contract change reduce preventative work on your route?</li>
                <li>• If the council knew condition was migrating towards red, why wasn't your defect caught?</li>
                <li>• Can the council fairly compare 2024 condition data to pre-2024 records in your defence?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue maintenance spend from Cheshire West's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£15,266k</td>
                  <td className="px-4 py-3 text-center">£18,266k</td>
                  <td className="px-4 py-3 text-center">£8,033k</td>
                  <td className="px-4 py-3 text-center">72%</td>
                  <td className="px-4 py-3 text-center">28%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£11,899k</td>
                  <td className="px-4 py-3 text-center">£14,899k</td>
                  <td className="px-4 py-3 text-center">£7,075k</td>
                  <td className="px-4 py-3 text-center">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£11,899k</td>
                  <td className="px-4 py-3 text-center">£14,899k</td>
                  <td className="px-4 py-3 text-center">£7,039k</td>
                  <td className="px-4 py-3 text-center">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£13,664k</td>
                  <td className="px-4 py-3 text-center">£15,714k</td>
                  <td className="px-4 py-3 text-center">£6,917k</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£13,161k</td>
                  <td className="px-4 py-3 text-center">£15,411k</td>
                  <td className="px-4 py-3 text-center">£6,106k</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£15,515k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£20,515k</td>
                  <td className="px-4 py-3 text-center font-bold">£6,610k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">79%</td>
                  <td className="px-4 py-3 text-center font-bold">21%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm">
            Every year, Cheshire West's capital spend exceeds its DfT allocation — by £3m to £5m.
            Preventative maintenance share has risen from 71% to a projected 79%. This is why the DfT
            awards a GREEN spend rating. But condition remains AMBER because spending more has not
            reversed the deterioration trend the council itself documents.
          </p>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Cheshire West invests seriously in its roads — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend consistently above DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual Gaist surveys on all road classes including U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Asset Management Strategy and lifecycle planning
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 79% of projected 2025/26 spend classed as preventative
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED more than doubled to 17.77% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED tripled from 5.29% to 16.87% under new survey standard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "continued migration" from good to poor across all classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 36,527 potholes filled in four years — defects form faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2023/24 contract change "limited the amount of preventative maintenance"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Best Practice AMBER — not the strongest asset management scorecard
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and annual Gaist surveys, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, cite the council's own 17.77% RED figure and annual survey data</li>
              <li>• Whether your road appeared in the Gaist grade 4 or 5 data before your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Cheshire West's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Cheshire West and Chester?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration data cited</li>
                <li>• No prior-report search</li>
                <li>• No methodology-change argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 17.77% U-road RED condition documented</li>
                <li>✅ B/C road decline and migration trend cited</li>
                <li>✅ 36,527 potholes in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Cheshire West</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=cheshire-west-and-chester"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Cheshire West Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cheshire West spends above its DfT allocation with a GREEN spend rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is
                road condition — and Cheshire West is AMBER overall because its network is deteriorating.
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired, not on how much the council spends in aggregate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,313km — 57.5% of the Cheshire West network — and are surveyed annually
                under Gaist condition surveys. RED-condition U-roads rose steadily from 6.84% in 2020 to
                8.35% in 2023, then jumped to 17.77% in 2024 after the council adopted an early version
                of the new national data standard. Even the pre-2024 trend shows one in twelve U-roads in
                the worst category.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2024 spike in RED condition figures weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — the council itself says it "opted to take [the new data standard] onboard early
                which can be seen reflected in this year's figures," so 2024 RED percentages are not
                directly comparable to earlier years. But the council also admits "a continued migration
                from the greens in grades 1 &amp; 2 towards the ambers in grades 3 &amp; 4" and "small
                increases in the grade 5 across the board" — documented deterioration regardless of the
                headline jump.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Best Practice AMBER mean the council has a strong Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                Cheshire West has a documented Asset Management Strategy, 100% annual Gaist surveys across
                all road classes, and ISO-aligned lifecycle planning — all of which support a Section 58
                defence. But Best Practice is AMBER, not GREEN, and the council acknowledges a new
                maintenance contract in 2023/24 "limited the amount of preventative maintenance we could
                undertake." Your claim still turns on the specific defect, not the council's paperwork.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cheshire West filled 36,527 potholes in four years — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council filled 9,837 potholes in 2021/22, 6,252 in 2022/23, 11,870 in 2023/24,
                and 8,568 in 2024/25 — a four-year total of 36,527 reactive repairs on a network where
                condition data shows continued decline. A network producing potholes at that rate is one
                where defects routinely form between inspections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.cheshirewestandchester.gov.uk/residents/highways/local-highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Cheshire West and Chester Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
